import {
  cloneElement,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { CustomTooltipProps } from './CustomTooltip.interface';

export const useCustomTooltip = (
  props: Pick<
    CustomTooltipProps,
    | 'title'
    | 'overflowWidth'
    | 'overflowLineNumber'
    | 'skipValidateOverflow'
    | 'children'
    | 'renderChildren'
  >,
) => {
  const {
    overflowWidth,
    overflowLineNumber,
    skipValidateOverflow,
    children,
    renderChildren,
  } = props;

  const childRef = useRef<HTMLDivElement | null>(null);
  const [overflow, setOverflow] = useState(false);

  // Determine if the content overflows
  const checkOverflow = useCallback(() => {
    const element = childRef.current;
    if (!element || skipValidateOverflow) return;

    const isOverflowing = overflowWidth
      ? element.scrollWidth > overflowWidth
      : overflowLineNumber
      ? element.scrollHeight > element.clientHeight
      : element.scrollHeight > element.clientHeight ||
        element.scrollWidth > element.clientWidth;

    setOverflow(isOverflowing);
  }, [overflowWidth, overflowLineNumber, skipValidateOverflow]);

  // Observe element resizing to re-check overflow
  useLayoutEffect(() => {
    const timeoutId = setTimeout(checkOverflow, 0);

    if (!childRef.current || skipValidateOverflow) {
      return () => clearTimeout(timeoutId);
    }

    const observer = new ResizeObserver(() => {
      setTimeout(checkOverflow, 0);
    });
    observer.observe(childRef.current);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [
    checkOverflow,
    skipValidateOverflow,
    props.title,
    overflowWidth,
    overflowLineNumber,
  ]);

  // Get styles for truncation based on props
  const getStyles = useCallback(() => {
    if (overflowLineNumber) {
      return {
        overflow: 'hidden',
        overflowWrap: 'anywhere',
        display: '-webkit-box',
        WebkitLineClamp: overflowLineNumber,
        WebkitBoxOrient: 'vertical',
        whiteSpace: 'pre-wrap',
        textOverflow: 'ellipsis',
      };
    }

    if (overflowWidth) {
      return {
        display: 'inline-block',
        maxWidth: overflowWidth,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      };
    }

    return {};
  }, [overflowWidth, overflowLineNumber]);

  const setChildRef = useCallback((node: HTMLDivElement | null) => {
    childRef.current = node;
  }, []);

  // Render children with proper styles and ref
  const childrenElement = useMemo(() => {
    if (skipValidateOverflow) return children;

    if (renderChildren) {
      return renderChildren(childRef, getStyles());
    }

    if (children && typeof children === 'object') {
      return cloneElement(children, {
        ref: setChildRef,
        style: {
          ...children.props?.style,
          ...getStyles(),
        },
      });
    }

    return null;
  }, [children, renderChildren, getStyles, setChildRef, skipValidateOverflow]);

  return {
    overflow,
    childrenElement,
  };
};
